/* Main Collection Products section — filters drawer + AJAX sort dropdown.
   Self-contained: no dependency on theme.js. A custom element is used (rather
   than a plain querySelectorAll pass) so the theme editor's section re-render
   re-runs connectedCallback automatically, keeping listeners wired after every
   settings change without a shopify:section:load hook. */

(function () {
  'use strict';

  if (customElements.get('main-collection-products-app')) return;

  class MainCollectionProductsApp extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('[data-section-id]');
      this.filterToggle = this.querySelector('[data-mcp-filter-open]');
      this.drawer = this.querySelector('[data-mcp-drawer]');
      this.closeTriggers = this.querySelectorAll('[data-mcp-drawer-close]');
      this.sortSelect = this.querySelector('[data-mcp-sort-select]');
      this.sortInput = this.querySelector('[data-mcp-sort-input]');
      this.viewButtons = this.querySelectorAll('[data-mcp-view-btn]');
      this.results = this.querySelector('[data-mcp-results]');
      this.filtersForm = this.querySelector('[data-mcp-filters-form]');
      this.lastFocused = null;
      this.fetchController = null;

      this.onFilterOpenClick = this.openDrawer.bind(this);
      this.onCloseTriggerClick = this.closeDrawer.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onSortChange = this.onSortChange.bind(this);
      this.onResultsClick = this.onResultsClick.bind(this);
      this.onPopState = this.onPopState.bind(this);
      this.onFiltersFormSubmit = this.onFiltersFormSubmit.bind(this);
      this.onViewButtonClick = this.onViewButtonClick.bind(this);

      if (this.filterToggle) this.filterToggle.addEventListener('click', this.onFilterOpenClick);
      this.closeTriggers.forEach((trigger) => trigger.addEventListener('click', this.onCloseTriggerClick));
      if (this.sortSelect) this.sortSelect.addEventListener('change', this.onSortChange);
      this.viewButtons.forEach((btn) => btn.addEventListener('click', this.onViewButtonClick));
      if (this.results) this.results.addEventListener('click', this.onResultsClick);
      if (this.filtersForm) this.filtersForm.addEventListener('submit', this.onFiltersFormSubmit);
      document.addEventListener('keydown', this.onKeydown);
      window.addEventListener('popstate', this.onPopState);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('popstate', this.onPopState);
    }

    openDrawer() {
      if (!this.drawer) return;

      this.lastFocused = document.activeElement;
      this.drawer.hidden = false;

      requestAnimationFrame(() => this.drawer.classList.add('is-open'));

      if (this.filterToggle) this.filterToggle.setAttribute('aria-expanded', 'true');
      document.documentElement.style.overflow = 'hidden';

      const focusTarget = this.drawer.querySelector('[data-mcp-drawer-close]');
      if (focusTarget) focusTarget.focus();
    }

    closeDrawer() {
      if (!this.drawer || !this.drawer.classList.contains('is-open')) return;

      this.drawer.classList.remove('is-open');
      if (this.filterToggle) this.filterToggle.setAttribute('aria-expanded', 'false');
      document.documentElement.style.overflow = '';

      const panel = this.drawer.querySelector('.main-collection-products__drawer-panel');
      const finish = () => {
        this.drawer.hidden = true;
      };

      if (panel) {
        panel.addEventListener('transitionend', finish, { once: true });
      } else {
        finish();
      }

      if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
        this.lastFocused.focus();
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.drawer && this.drawer.classList.contains('is-open')) {
        this.closeDrawer();
      }
    }

    // Pagination links live inside the AJAX-swapped results container, so a
    // plain full-page click still works out of the box (they're real <a>
    // hrefs) — this intercepts them to keep pager clicks on the same fast,
    // no-reload path as sorting, without touching the currently applied
    // sort_by or filters (both already baked into each link's href server-side).
    onResultsClick(event) {
      const link = event.target.closest('a.main-collection-products__pagination-link');
      if (!link) return;

      event.preventDefault();
      this.fetchResults(link.href, { scrollToResults: true });
    }

    onSortChange(event) {
      const value = event.target.value;
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', value);
      url.searchParams.delete('page');

      if (this.sortInput) this.sortInput.value = value;

      this.fetchResults(url.toString());
    }

    onPopState() {
      this.fetchResults(window.location.href, { pushState: false });
    }

    // Mobile-only grid density toggle. Lives in the toolbar (outside the
    // AJAX-swapped results container) and flips a data attribute on the
    // section root — also untouched by result swaps — so the chosen density
    // survives sorting/filtering/pagination without extra rebinding.
    onViewButtonClick(event) {
      const btn = event.currentTarget;
      const columns = btn.dataset.columns;

      this.viewButtons.forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));

      if (this.section) {
        this.section.setAttribute('data-mcp-mobile-view', columns);
      }
    }

    onFiltersFormSubmit(event) {
      event.preventDefault();

      const url = new URL(this.filtersForm.action, window.location.origin);
      const params = new URLSearchParams(new FormData(this.filtersForm));
      url.search = params.toString();

      this.fetchResults(url.toString(), { scrollToResults: true });
      this.closeDrawer();
    }

    fetchResults(targetUrl, options) {
      const opts = options || {};
      const pushState = opts.pushState !== false;
      const scrollToResults = opts.scrollToResults === true;

      if (!this.results) {
        window.location.href = targetUrl;
        return;
      }

      const sectionId = this.section ? this.section.dataset.sectionId : null;
      if (!sectionId) {
        window.location.href = targetUrl;
        return;
      }

      const fetchUrl = new URL(targetUrl, window.location.origin);
      fetchUrl.searchParams.set('section_id', sectionId);

      if (this.fetchController) this.fetchController.abort();
      this.fetchController = new AbortController();

      this.results.setAttribute('aria-busy', 'true');

      fetch(fetchUrl.toString(), { signal: this.fetchController.signal })
        .then((response) => {
          if (!response.ok) throw new Error('Request failed: ' + response.status);
          return response.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newResults = doc.querySelector('[data-mcp-results]');
          if (!newResults) throw new Error('Results container not found in response');

          this.results.innerHTML = newResults.innerHTML;
          this.results.removeAttribute('aria-busy');

          if (scrollToResults) {
            const resultsTop = this.results.getBoundingClientRect().top;
            if (window.theme && typeof window.theme.scrollTo === 'function') {
              window.theme.scrollTo(resultsTop);
            } else {
              this.results.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }

          if (pushState) {
            const cleanUrl = new URL(targetUrl, window.location.origin);
            cleanUrl.searchParams.delete('section_id');
            window.history.pushState({}, '', cleanUrl.toString());
          }
        })
        .catch((error) => {
          if (error.name === 'AbortError') return;
          window.location.href = targetUrl;
        });
    }
  }

  customElements.define('main-collection-products-app', MainCollectionProductsApp);
})();
